import { FC, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "react-i18next";
import { PermitVerificationStatus, PropertyVerificationResult } from "@services";
import { VerificationCard, VerificationPayload } from "./VerificationCard";

/**
 * MS-488 — the "توثيق وزارة السياحة" section on the property-details edit page.
 * First section (above الصور). Four states driven by the property's stored
 * verification status + expiry: verified / unverified / near-expiry / expired.
 * Verified & near-expiry show a details block with an "تعديل" action; unverified
 * & expired show the shared verify card inline.
 *
 * The status/expiry come from the property details response; until the backend
 * exposes them everything reads as unverified (the card is shown to verify).
 */
export interface VerificationSectionProps {
  propertyId: number;
  status?: number | null;
  permitNumber?: string | null;
  expiryDate?: string | null;
  ownerNameAr?: string | null;
  ownerNameEn?: string | null;
  maskedOwnerId?: string | null;
  ownerIdentityId?: number | null;
  /** Refetch the property after a successful re-verify. */
  onChanged?: () => void;
}

type DisplayState = "unverified" | "verified" | "nearExpiry" | "expired";
const NEAR_EXPIRY_DAYS = 30;
const fmtDate = (d?: string | null) => (d ? d.slice(0, 10) : "");
const maskLocal = (id: string) => (id.length > 4 ? `•••• ${id.slice(-4)}` : id);

const C = {
  label: "#1F1B3A",
  muted: "#6B7280",
  primary: "#5B3FFF",
  verifiedBg: "#F0FDF4",
  verifiedBorder: "#BBF7D0",
  verifiedText: "#16A34A",
  verifiedBadgeBg: "#DCFCE7",
  amberBg: "#FEFCE8",
  amberBorder: "#FDE68A",
  amberText: "#B45309",
  amberBadgeBg: "#FEF3C7",
  redBg: "#FEF2F2",
  redBorder: "#FECACA",
  redText: "#DC2626",
  redBadgeBg: "#FEE2E2",
  grayBadgeBg: "#F3F4F6",
  grayText: "#6B7280",
};

export const VerificationSection: FC<VerificationSectionProps> = ({
  propertyId,
  status,
  permitNumber,
  expiryDate,
  ownerNameAr,
  ownerNameEn,
  maskedOwnerId,
  ownerIdentityId,
  onChanged,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  // Local override so a successful re-verify updates the UI immediately even
  // before the property refetch returns the new status.
  const [override, setOverride] = useState<{
    status: number;
    name?: string | null;
    permit?: string | null;
    expiry?: string | null;
    maskedId?: string | null;
  } | null>(null);

  const eff = {
    status: override?.status ?? status,
    permit: override?.permit ?? permitNumber,
    expiry: override?.expiry ?? expiryDate,
    name: override?.name ?? ownerNameAr ?? ownerNameEn,
    maskedId: override?.maskedId ?? maskedOwnerId,
  };

  const state: DisplayState = useMemo(() => {
    if (eff.status !== PermitVerificationStatus.Verified) return "unverified";
    if (eff.expiry) {
      const exp = new Date(eff.expiry).getTime();
      const now = Date.now();
      if (exp < now) return "expired";
      if (exp - now < NEAR_EXPIRY_DAYS * 86_400_000) return "nearExpiry";
    }
    return "verified";
  }, [eff.status, eff.expiry]);

  // Card reports the payload (permit + owner source) synchronously just before
  // onVerified, so a ref captures it for the success override.
  const capturedRef = useRef<VerificationPayload | null>(null);
  const setCaptured = (p: VerificationPayload | null) => {
    capturedRef.current = p;
  };

  const handleVerified = (res: PropertyVerificationResult) => {
    const cap = capturedRef.current;
    setOverride({
      status: PermitVerificationStatus.Verified,
      name: res.verifiedNameAr ?? res.verifiedNameEn,
      permit: cap?.permitNumber ?? eff.permit,
      expiry: res.expiryDate,
      maskedId:
        cap?.ownerIdentityId != null
          ? maskedOwnerId
          : cap?.idNumber
          ? maskLocal(cap.idNumber)
          : eff.maskedId,
    });
    setEditing(false);
    onChanged?.();
  };

  const badge = {
    unverified: { text: t("PERMIT_VERIFICATION.STATUS_UNVERIFIED"), bg: C.grayBadgeBg, color: C.grayText },
    verified: { text: t("PERMIT_VERIFICATION.STATUS_VERIFIED"), bg: C.verifiedBadgeBg, color: C.verifiedText },
    nearExpiry: { text: t("PERMIT_VERIFICATION.STATUS_NEAR_EXPIRY"), bg: C.amberBadgeBg, color: C.amberText },
    expired: { text: t("PERMIT_VERIFICATION.STATUS_EXPIRED"), bg: C.redBadgeBg, color: C.redText },
  }[state];

  const showEditBtn = (state === "verified" || state === "nearExpiry") && !editing;

  const card = (initialPermit?: string) => (
    <VerificationCard
      surface="dashboard"
      propertyId={propertyId}
      initialPermit={initialPermit}
      suppressSuccessView
      onVerificationChange={setCaptured}
      onVerified={handleVerified}
    />
  );

  /** Verified / near-expiry / expired details block. */
  const detailsBlock = (variant: "verified" | "nearExpiry" | "expired") => {
    const style =
      variant === "verified"
        ? { bg: C.verifiedBg, border: C.verifiedBorder, head: C.verifiedText }
        : variant === "nearExpiry"
        ? { bg: C.amberBg, border: C.amberBorder, head: C.amberText }
        : { bg: C.redBg, border: C.redBorder, head: C.redText };
    const Icon = variant === "verified" ? CheckCircleIcon : variant === "nearExpiry" ? AccessTimeIcon : CancelIcon;
    const head =
      variant === "verified"
        ? t("PERMIT_VERIFICATION.SUCCESS_BADGE")
        : variant === "nearExpiry"
        ? t("PERMIT_VERIFICATION.NEAR_EXPIRY_TITLE")
        : t("PERMIT_VERIFICATION.EXPIRED_TITLE");
    const dateLabel =
      variant === "verified"
        ? t("PERMIT_VERIFICATION.VALID_UNTIL")
        : variant === "nearExpiry"
        ? t("PERMIT_VERIFICATION.ENDS_ON")
        : t("PERMIT_VERIFICATION.ENDED_ON");
    const sep = variant === "verified" ? " " : ": ";
    return (
      <Box sx={{ backgroundColor: style.bg, border: `1px solid ${style.border}`, borderRadius: "16px", p: "20px", textAlign: "start" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "12px" }}>
          <Icon sx={{ color: style.head, fontSize: 22 }} />
          <Typography sx={{ color: style.head, fontWeight: 800, fontSize: 15 }}>{head}</Typography>
        </Box>
        <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 2 }}>
          {t("PERMIT_VERIFICATION.OWNER_NAME")}{sep}{eff.name}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 2 }}>
          {t("PERMIT_VERIFICATION.PERMIT_NUMBER")}{sep}{eff.permit}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 2 }}>
          {t("PERMIT_VERIFICATION.OWNER_ID")}{sep}{eff.maskedId}
        </Typography>
        <Typography sx={{ fontSize: 14, color: style.head, fontWeight: 700, lineHeight: 2 }}>
          {dateLabel} {fmtDate(eff.expiry)}
        </Typography>
        {variant === "nearExpiry" && (
          <>
            <Box sx={{ height: "1px", backgroundColor: style.border, my: "12px" }} />
            <Typography sx={{ fontSize: 13, color: C.amberText }}>
              {t("PERMIT_VERIFICATION.NEAR_EXPIRY_NUDGE")}
            </Typography>
          </>
        )}
      </Box>
    );
  };

  const infoLine = (text: string, color: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "12px", color }}>
      <Box sx={{ fontSize: 14 }}>ⓘ</Box>
      <Typography sx={{ fontSize: 13 }}>{text}</Typography>
    </Box>
  );

  return (
    <Box>
      {/* Section header: title + badge (start) · edit (end) */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "16px" }}>
        <Box sx={{ textAlign: "start" }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: C.label }}>
            {t("PERMIT_VERIFICATION.SECTION_TITLE")}
          </Typography>
          <Box
            sx={{
              display: "inline-block",
              mt: 1,
              backgroundColor: badge.bg,
              color: badge.color,
              fontSize: 12,
              fontWeight: 700,
              borderRadius: "100px",
              px: "12px",
              py: "4px",
            }}
          >
            {badge.text}
          </Box>
        </Box>
        {showEditBtn && (
          <Box
            component="button"
            type="button"
            onClick={() => setEditing(true)}
            sx={{
              flexShrink: 0,
              backgroundColor: C.primary,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              px: "20px",
              py: "10px",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#4930CC" },
            }}
          >
            {t("PERMIT_VERIFICATION.EDIT")}
          </Box>
        )}
      </Box>

      {/* Body */}
      {editing ? (
        <>
          {card(eff.permit ?? "")}
          <Box
            component="button"
            type="button"
            onClick={() => setEditing(false)}
            sx={{ mt: "12px", background: "none", border: "none", color: C.primary, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
          >
            {t("PERMIT_VERIFICATION.CANCEL")}
          </Box>
        </>
      ) : state === "unverified" ? (
        <>
          {card()}
          {infoLine(t("PERMIT_VERIFICATION.INFO_UNVERIFIED"), C.muted)}
        </>
      ) : state === "verified" ? (
        detailsBlock("verified")
      ) : state === "nearExpiry" ? (
        detailsBlock("nearExpiry")
      ) : (
        <>
          {detailsBlock("expired")}
          <Box sx={{ mt: "16px" }}>{card(eff.permit ?? "")}</Box>
          {infoLine(t("PERMIT_VERIFICATION.INFO_EXPIRED"), C.redText)}
        </>
      )}
    </Box>
  );
};

export default VerificationSection;
